import type { AuditActorType, AuditEvent, AuditOutcome, ResourceType } from "@webyonetim/contracts";
import { useQuery } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";
import { useSearchParams } from "react-router";

import { PageHeader } from "../../components/shell/AppShell";
import { Button, SelectField, TextField } from "../../components/ui/Controls";
import { EmptyState, ErrorState, Skeleton, StatusBadge } from "../../components/ui/Feedback";
import { CodeValue, ResponsiveData, type DataColumn } from "../../components/ui/ResponsiveData";
import { formatDateTime } from "../../lib/format";
import { auditApi } from "../../lib/api";
import { AuditDetail } from "./AuditDetail";

function outcomeLabel(outcome: AuditOutcome): string {
  return outcome === "succeeded" ? "Başarılı" : outcome === "failed" ? "Başarısız" : "Reddedildi";
}

export function AuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<AuditEvent>();
  const [from, setFrom] = useState(searchParams.get("from")?.slice(0, 10) ?? "");
  const [to, setTo] = useState(searchParams.get("to")?.slice(0, 10) ?? "");
  const [actorType, setActorType] = useState(searchParams.get("actorType") ?? "");
  const [actorId, setActorId] = useState(searchParams.get("actorId") ?? "");
  const [action, setAction] = useState(searchParams.get("action") ?? "");
  const [resourceType, setResourceType] = useState(searchParams.get("resourceType") ?? "");
  const [outcome, setOutcome] = useState(searchParams.get("outcome") ?? "");
  const [requestOrCorrelationId, setRequestOrCorrelationId] = useState(
    searchParams.get("requestOrCorrelationId") ?? "",
  );
  const queryString = searchParams.toString();
  const query = useQuery({
    queryKey: ["audit-events", queryString],
    queryFn: () => auditApi.list(new URLSearchParams(queryString)),
    retry: false,
  });
  const applyFilters = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams();
    if (from !== "") next.set("from", `${from}T00:00:00.000Z`);
    if (to !== "") next.set("to", `${to}T23:59:59.999Z`);
    if (actorType !== "") next.set("actorType", actorType);
    if (actorId !== "") next.set("actorId", actorId);
    if (action !== "") next.set("action", action);
    if (resourceType !== "") next.set("resourceType", resourceType);
    if (outcome !== "") next.set("outcome", outcome);
    if (requestOrCorrelationId !== "") next.set("requestOrCorrelationId", requestOrCorrelationId);
    next.set("sort", "occurred_at_desc");
    setSearchParams(next);
  };
  const clearFilters = () => {
    setFrom("");
    setTo("");
    setActorType("");
    setActorId("");
    setAction("");
    setResourceType("");
    setOutcome("");
    setRequestOrCorrelationId("");
    setSearchParams({});
  };
  const columns: readonly DataColumn<AuditEvent>[] = [
    {
      header: "Zaman",
      key: "time",
      render: (item) => <time dateTime={item.occurredAt}>{formatDateTime(item.occurredAt)}</time>,
    },
    {
      header: "Aktör",
      key: "actor",
      render: (item) => (
        <>
          {item.actor.label}
          <br />
          <span className="muted">{item.actor.type}</span>
        </>
      ),
    },
    { header: "İşlem", key: "action", render: (item) => <CodeValue>{item.action}</CodeValue> },
    {
      header: "Hedef",
      key: "target",
      render: (item) =>
        item.target === undefined ? (
          "Hedef yok"
        ) : (
          <>
            {item.target.label ?? item.target.type}
            <br />
            <CodeValue>{item.target.id}</CodeValue>
          </>
        ),
    },
    {
      header: "Sonuç",
      key: "outcome",
      render: (item) => (
        <StatusBadge
          label={outcomeLabel(item.outcome)}
          status={
            item.outcome === "succeeded"
              ? "succeeded"
              : item.outcome === "failed"
                ? "failed"
                : "warning"
          }
        />
      ),
    },
    {
      header: "Korelasyon kimliği",
      key: "correlation",
      render: (item) => <CodeValue>{item.correlationId}</CodeValue>,
    },
    {
      header: "Ayrıntı",
      key: "detail",
      render: (item) => (
        <Button
          onClick={() => {
            setSelected(item);
          }}
          variant="secondary"
        >
          Ayrıntıyı aç
        </Button>
      ),
    },
  ];
  const hasFilters = [...searchParams.keys()].some((key) => key !== "sort" && key !== "cursor");
  return (
    <div className="page">
      <PageHeader
        description="Kimlik, yetki ve sistem işlemlerinin değiştirilemez geçmişi."
        title="Denetim Kaydı"
      />
      <form className="card form-stack" onSubmit={applyFilters}>
        <h2>Filtreler</h2>
        <div className="two-column">
          <TextField
            label="Başlangıç tarihi"
            onChange={(event) => {
              setFrom(event.currentTarget.value);
            }}
            type="date"
            value={from}
          />
          <TextField
            label="Bitiş tarihi"
            onChange={(event) => {
              setTo(event.currentTarget.value);
            }}
            type="date"
            value={to}
          />
          <SelectField
            label="Aktör türü"
            onChange={(event) => {
              setActorType(event.currentTarget.value);
            }}
            value={actorType}
          >
            <option value="">Tümü</option>
            {(
              ["user", "api_key", "system", "agent", "webhook"] satisfies readonly AuditActorType[]
            ).map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </SelectField>
          <TextField
            label="Aktör kimliği"
            onChange={(event) => {
              setActorId(event.currentTarget.value);
            }}
            value={actorId}
          />
          <TextField
            label="İşlem"
            onChange={(event) => {
              setAction(event.currentTarget.value);
            }}
            value={action}
          />
          <SelectField
            label="Kaynak türü"
            onChange={(event) => {
              setResourceType(event.currentTarget.value);
            }}
            value={resourceType}
          >
            <option value="">Tümü</option>
            {(
              [
                "tenant",
                "user",
                "membership",
                "invitation",
                "session",
                "api_key",
                "job",
                "operation",
                "project",
                "environment",
                "service",
                "host",
                "source_snapshot",
                "build",
                "release",
                "deployment",
                "domain",
                "database",
                "secret",
              ] satisfies readonly ResourceType[]
            ).map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Sonuç"
            onChange={(event) => {
              setOutcome(event.currentTarget.value);
            }}
            value={outcome}
          >
            <option value="">Tümü</option>
            <option value="succeeded">Başarılı</option>
            <option value="failed">Başarısız</option>
            <option value="denied">Reddedildi</option>
          </SelectField>
          <TextField
            label="İstek veya korelasyon kimliği"
            onChange={(event) => {
              setRequestOrCorrelationId(event.currentTarget.value);
            }}
            value={requestOrCorrelationId}
          />
        </div>
        <div className="form-actions">
          <Button type="submit">Filtreleri uygula</Button>
          <Button onClick={clearFilters} variant="secondary">
            Filtreleri temizle
          </Button>
        </div>
      </form>
      {query.isLoading ? (
        <Skeleton />
      ) : query.isError ? (
        <ErrorState
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : query.data?.items.length === 0 ? (
        <EmptyState
          action={
            hasFilters ? (
              <Button onClick={clearFilters} variant="secondary">
                Filtreleri temizle
              </Button>
            ) : undefined
          }
          body={
            hasFilters
              ? "Filtreleri değiştirin veya tarih aralığını genişletin."
              : "Güvenlik ve sistem işlemleri gerçekleştikçe burada görünecek."
          }
          heading={hasFilters ? "Bu filtrelerle kayıt bulunamadı" : "Henüz denetim kaydı yok"}
        />
      ) : query.data === undefined ? null : (
        <section className="card stack">
          <ResponsiveData
            caption="Denetim kayıtları, en yeniden eskiye"
            columns={columns}
            getKey={(item) => item.id}
            items={query.data.items}
            mobileHeading={(item) => formatDateTime(item.occurredAt)}
          />
          {query.data.page.hasMore && query.data.page.nextCursor !== undefined ? (
            <Button
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.set("cursor", query.data.page.nextCursor ?? "");
                setSearchParams(next);
              }}
              variant="secondary"
            >
              Daha fazla kayıt göster
            </Button>
          ) : null}
        </section>
      )}
      {selected === undefined ? null : (
        <AuditDetail
          event={selected}
          onOpenChange={(open) => {
            if (!open) setSelected(undefined);
          }}
          open
        />
      )}
    </div>
  );
}
